/**
 * Gym Shorts v4 PA2 ("strip") — arrow controls for the design-switcher strip.
 *
 * <gs-pa2-strip> wraps the horizontal thumbnail track rendered by
 * snippets/gym-shorts-v4-pa2-design-strip.liquid. Each arrow scrolls the
 * track by ~80% of its visible width; an arrow hides once its end of the
 * track is reached, so their mere presence tells the customer there are
 * more designs off-screen. Swipe scrolling keeps working as before —
 * arrows and scroll state stay in sync via the (passive) scroll listener.
 */
(() => {
  class GsPa2Strip extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gs-pa2-track]');
      this.prevBtn = this.querySelector('[data-gs-pa2-prev]');
      this.nextBtn = this.querySelector('[data-gs-pa2-next]');
      if (!this.track || !this.prevBtn || !this.nextBtn) return;

      this._onPrev = () => this._scrollBy(-1);
      this._onNext = () => this._scrollBy(1);
      this._onScroll = () => this._update();
      this.prevBtn.addEventListener('click', this._onPrev);
      this.nextBtn.addEventListener('click', this._onNext);
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll);

      // Initial state — after layout (thumbnails may still be sizing).
      this._update();
      requestAnimationFrame(() => this._update());
    }
    disconnectedCallback() {
      this.prevBtn?.removeEventListener('click', this._onPrev);
      this.nextBtn?.removeEventListener('click', this._onNext);
      this.track?.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }
    _scrollBy(dir) {
      this.track.scrollBy({ left: dir * this.track.clientWidth * 0.8, behavior: 'smooth' });
    }
    _update() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      const atStart = x <= 2;
      const atEnd = x >= maxScroll - 2;
      this.prevBtn.classList.toggle('is-hidden', atStart);
      this.nextBtn.classList.toggle('is-hidden', atEnd || maxScroll <= 0);
      this.classList.toggle('is-scrollable', maxScroll > 0);
      this.classList.toggle('is-at-start', atStart);
      this.classList.toggle('is-at-end', atEnd);
    }
  }
  if (!customElements.get('gs-pa2-strip')) {
    customElements.define('gs-pa2-strip', GsPa2Strip);
  }

  // ---------------------------------------------------------------------------
  // Prompt rewrite — the pack-grid prompt text is produced by the shared
  // gym-shorts-v4.js picker ("Pick N more designs to complete your pack").
  // PA2 also lets customers fill the pack via the qty stepper, so mention it —
  // rewritten here (PA2-only page) instead of editing the shared v4 script.
  // Rewritten text no longer matches the pattern, so the observer can't loop.
  // ---------------------------------------------------------------------------
  const PROMPT_RE = /^Pick (\d+) more designs? to complete your pack$/;
  function initPromptRewrite() {
    const el = document.querySelector('[data-gs-v4-prompt]');
    if (!el) return;
    const rewrite = () => {
      const m = (el.textContent || '').trim().match(PROMPT_RE);
      if (m) el.textContent = `Pick ${m[1]} more or increase quantity to complete your pack`;
    };
    new MutationObserver(rewrite).observe(el, { childList: true, characterData: true, subtree: true });
    rewrite();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPromptRewrite, { once: true });
  } else {
    initPromptRewrite();
  }
})();
